import { useMutation } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ShieldCheck } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import { verifyTotp } from '@/api/auth-api'
import { useAuth } from '@/auth/use-auth'
import { ProblemAlert } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { AuthLayout } from './auth-layout'

const CODE_LENGTH = 6

/**
 * The second factor.
 *
 * TOTP is mandatory from day one for every user holding an approval, administration, or teller
 * permission, and there is no SMS fallback for staff: a one-time code delivered to a phone number
 * is only as strong as the network operator's resistance to a SIM swap.
 *
 * The challenge token this screen spends is short-lived and single-use. Abandoning the screen
 * abandons the attempt — there is no half-signed-in state to return to.
 */
export function MfaChallengePage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const search = useSearch({ from: '/mfa' })
  const [code, setCode] = useState('')

  const challenge = auth.challenge

  const mutation = useMutation({
    mutationFn: verifyTotp,
    onSuccess: (outcome) => {
      auth.applyOutcome(outcome, challenge?.username ?? '')

      if (outcome.outcome === 'password_change_required') {
        void navigate({ to: '/password/change', search })
      } else if (outcome.outcome === 'authenticated') {
        void (search.redirect ? navigate({ href: search.redirect }) : navigate({ to: '/' }))
      }
    },
  })

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!challenge) {
      return
    }

    mutation.mutate({ challengeToken: challenge.token, code })
  }

  return (
    <AuthLayout
      title="Two-factor verification"
      description={`Enter the current code from your authenticator app${
        challenge?.username ? ` for ${challenge.username}` : ''
      }.`}
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="totp-code">Authentication code</Label>
          <Input
            id="totp-code"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={CODE_LENGTH}
            placeholder="000000"
            className="numeric h-11 text-center text-lg tracking-[0.5em] tabular-nums"
            aria-describedby="totp-help"
          />
          <p id="totp-help" className="text-xs text-muted">
            Codes change every 30 seconds. If the code is rejected, wait for the next one rather than
            retyping the same digits.
          </p>
        </div>

        {mutation.isError ? <ProblemAlert error={mutation.error} /> : null}

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          disabled={mutation.isPending || code.length < CODE_LENGTH}
        >
          <ShieldCheck />
          {mutation.isPending ? 'Verifying…' : 'Verify'}
        </Button>

        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={() => {
            auth.clearChallenge()
            void navigate({ to: '/login' })
          }}
        >
          Start again
        </Button>
      </form>
    </AuthLayout>
  )
}
